import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { animateScroll as scroll, scroller } from 'react-scroll/modules';

import styles from './AgeGate.module.scss';
import { IAgeGateProps } from './interface';

// Helpers
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { generateArrayOfYears, getAge } from '../../../utils/dateFormatter';

// Assets
import logo from '../../../assets/logo.svg';
import { FiChevronDown } from 'react-icons/fi';

//Components
import Button from '../Button';
import { RootState } from '../../../app/store';
import { setUserData } from '../../../slices/userSlice';
import InputCheckbox from '../../InputCheckbox';
import { useSearchParams } from 'react-router-dom';
import { steps } from '../../../containers/PageRouter/config';

const body = document.querySelector('body')!;

const AgeGate: React.FC<IAgeGateProps> = (props: IAgeGateProps) => {
    const dispatch = useDispatch();

    const [isVisible, setVisibility] = useState(false);
    const [eligible, setEligibility] = useState(true);

    const [years, setYears] = useState<number[]>([]);
    const [formData, setFormData] = useState({
        month: null,
        year: null,
        nicotineUser: false
    });

    const [searchParams, _] = useSearchParams();
    const utmSource = searchParams.get('utm_source');

    const userData = useSelector((state: RootState) => state.users.userData);

    useEffect(() => {
        setYears(generateArrayOfYears());
        if (
            userData.monthOfBirth &&
            userData.yearOfBirth &&
            getAge(`${userData.yearOfBirth}/${userData.monthOfBirth}/01`) >= 18
        ) {
            setVisibility(false);
            enableBodyScroll(body);
        } else {
            setVisibility(true);
            disableBodyScroll(body);
        }
    }, [userData]);

    const handleChange = (field: string, value: any) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const handleAccept = () => {
        const userAge = getAge(`${formData.year}/${formData.month}/01`);
        const eligibility = userAge >= 18;
        setEligibility(userAge >= 18);
        if (eligibility) {
            dispatch(
                setUserData({
                    ...userData,
                    monthOfBirth: formData.month,
                    yearOfBirth: formData.year,
                    nicotineUser: formData.nicotineUser ? '1' : '0'
                })
            );
            setVisibility(false);
            enableBodyScroll(body);
            if (
                userData?.stepHomepage === steps.STEP_START &&
                (utmSource === 'LAMP_QR_CODE_IRINA' || utmSource === 'BR_QR_CODE_IRINA')
            ) {
                scroller.scrollTo('qrIrina', {
                    duration: 250,
                    delay: 0,
                    offset: -100,
                    smooth: true
                });
            } else {
                scroll.scrollToTop();
            }
        }
    };

    useEffect(() => {
        if (isVisible) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';

            setFormData({
                month: null,
                year: null,
                nicotineUser: false
            });
        }
    }, [isVisible]);

    if (isVisible) {
        return (
            <div className={styles.ageGate}>
                <img src={logo} alt="iqos-club" className={styles.logo} />

                <div className={styles.box}>
                    {eligible ? (
                        <>
                            <h2>
                                Introdu data nașterii pentru a confirma că ești adult (peste 18
                                ani).
                            </h2>

                            <div className={styles.form}>
                                <div className={styles.row}>
                                    <div className={styles.container}>
                                        <label>Luna</label>
                                        <select
                                            name="month"
                                            value={formData.month || ''}
                                            onChange={(e) => handleChange('month', e.target.value)}
                                        >
                                            <option value="" disabled>
                                                Selectează
                                            </option>

                                            <option value="1">Ianuarie</option>
                                            <option value="2">Februarie</option>
                                            <option value="3">Martie</option>
                                            <option value="4">Aprilie</option>
                                            <option value="5">Mai</option>
                                            <option value="6">Iunie</option>
                                            <option value="7">Iulie</option>
                                            <option value="8">August</option>
                                            <option value="9">Septembrie</option>
                                            <option value="10">Octombrie</option>
                                            <option value="11">Noiembrie</option>
                                            <option value="12">Decembrie</option>
                                        </select>
                                        <FiChevronDown size={22} className={styles.selectArrow} />
                                    </div>

                                    <div className={styles.container}>
                                        <label>An</label>
                                        <select
                                            value={formData.year || ''}
                                            onChange={(e) => handleChange('year', e.target.value)}
                                        >
                                            <option value="" disabled>
                                                Selectează
                                            </option>

                                            {years.map((year) => (
                                                <option key={year} value={year}>
                                                    {year}
                                                </option>
                                            ))}
                                        </select>
                                        <FiChevronDown size={22} className={styles.selectArrow} />
                                    </div>
                                </div>
                                <InputCheckbox
                                    isChecked={formData.nicotineUser}
                                    id="agree"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        handleChange('nicotineUser', e.target.checked)
                                    }
                                    extraClasses={styles.ageGateCheckbox}
                                >
                                    Confirm că sunt utilizator de produse cu nicotină, rezident în
                                    România.
                                </InputCheckbox>
                            </div>

                            <Button
                                type="solid"
                                color="light"
                                hover="turquoise"
                                label="Confirmă"
                                onClick={handleAccept}
                                disabled={
                                    !formData.year || !formData.month || !formData.nicotineUser
                                }
                            />
                        </>
                    ) : (
                        <h2>
                            Trebuie să ai peste 18 ani <br />
                            pentru a accesa acest website.
                        </h2>
                    )}

                    <p>
                        <small>
                            Acest website conține informații despre produsele fără fum ale Philip
                            Morris Trading SRL, destinate exclusiv adulților din România care altfel
                            ar continua să folosească alte produse cu nicotină convenționale (i.e.
                            care nu sunt fără fum). Produsele fără fum nu sunt alternative pentru
                            renunțarea la consumul de produse cu nicotină convenționale, și nu au
                            fost create ca facilitatori pentru o astfel de renunțare. Produsele fără
                            fum nu sunt lipsite de riscuri și conțin nicotină, care creează
                            dependență. Vă rugăm să vizitați pagina "Important" pentru informații
                            suplimentare despre riscurile IQOS.
                        </small>
                    </p>
                </div>
            </div>
        );
    }

    return null;
};

export default AgeGate;
